import { useEffect, useRef, useState } from 'react';
import { cn } from '~/lib/utils';
import styles from './EditableText.module.css';
import { useIsomorphicLayoutEffect } from '~/lib/useIsomorphicLayoutEffect';

export interface EditableTextProps {
  /** The committed name. Re-seeds the draft every time editing opens. */
  value: string;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  /**
   * A name the user actually changed. Never fires for an empty string or for the name it already
   * had — both are ways of saying "leave it alone", and a caller that had to filter them would
   * write a state update and a re-render for a rename that did not happen.
   */
  onCommit: (next: string) => void;
  /** Accessible name for the field. There is no visible label — the text IS the label. */
  label: string;
  /**
   * How wide the field is allowed to grow. Omit and it takes its container, which is right for a
   * name that already fills a column; give it a number and the field tracks the text up to that
   * ceiling, which is right for a name sitting inline with something after it.
   */
  maxWidth?: number;
  /**
   * The resting text opens itself on a single click, as a real button.
   *
   * A button because a name you can press is a control, whatever it looks like: this is what gives
   * it a tab stop, an Enter and a Space, a focus ring and an accessible name, none of which a
   * `<span>` with an `onClick` has. It is styled back down to being the text — the only thing it
   * keeps is an I-beam cursor.
   *
   * Omit it and the text is inert, which is what a name inside a bigger target wants: a Directory
   * row takes the double-click on the ROW, so any part of the row works rather than just the eleven
   * characters of a short title.
   */
  activate?: boolean;
  /** Applied to BOTH states, so the field is the text it replaces rather than a box near it. */
  className?: string;
}

/**
 * A name you can edit in place, and nothing else.
 *
 * Enter commits, Escape reverts, clicking away commits — the contract `Header/TitleBlock` already
 * set for the deck name, restated here because two more surfaces now need it and a third
 * hand-rolled copy is how three renames end up behaving three ways.
 *
 * It inherits its type. Every rule that matters — family, size, weight, tracking, colour — comes
 * from whatever the caller wraps it in, because the whole point is that the field IS the text: at
 * the moment it opens nothing may move, resize or change tone. `className` lands on both states for
 * the same reason.
 */
export function EditableText({
  value,
  editing,
  onEditingChange,
  onCommit,
  label,
  maxWidth,
  activate,
  className,
}: EditableTextProps) {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const sizerRef = useRef<HTMLSpanElement>(null);
  const [fieldWidth, setFieldWidth] = useState<number | null>(null);

  // Re-seed on entry, so a previous escape can never leak into the next rename.
  useEffect(() => {
    if (editing) setDraft(value);
  }, [editing, value]);

  useEffect(() => {
    if (!editing) return;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    // `select()` leaves the selection's focus end at the LAST character and the browser scrolls
    // there to keep it in view, so opening a name longer than the field swaps its first words for
    // its last ones. Selecting BACKWARD puts the focus end at character 0 and the field stays where
    // the text was. Same selection either way, and the words do not move.
    input.setSelectionRange(0, input.value.length, 'backward');
    // Chrome scrolls to the selection's end regardless of direction, so pin it back.
    input.scrollLeft = 0;
  }, [editing]);

  /*
   * Measured in a LAYOUT effect, and that is the whole trick.
   *
   * React has already committed the new text to the input, and this runs before the browser paints,
   * so the box and the text it is sized to reach the screen in the same frame. A frame of lag is a
   * frame in which the text is wider than its own scroller, which a focused input answers by
   * scrolling — and the name visibly slides. For the same reason the width is never transitioned:
   * see `TitleBlock.module.css`, where animating it made the deck name jitter left and right on
   * every keystroke. A box that tracks text cannot lag the text it tracks.
   */
  useIsomorphicLayoutEffect(() => {
    if (!editing || maxWidth === undefined) {
      setFieldWidth(null);
      return;
    }
    const sizer = sizerRef.current;
    if (!sizer) return;
    setFieldWidth(Math.min(sizer.getBoundingClientRect().width, maxWidth));
  }, [editing, draft, maxWidth]);

  if (!editing) {
    if (!activate) return <span className={className}>{value}</span>;
    return (
      <button
        type="button"
        className={cn(className, styles.activatable)}
        aria-label={label}
        onClick={() => onEditingChange(true)}
      >
        {value}
      </button>
    );
  }

  const commit = () => {
    const next = draft.trim();
    if (next && next !== value) onCommit(next);
    onEditingChange(false);
  };

  return (
    <span className={cn(styles.wrap, className)}>
      {/* A ruler, not a layout participant: out of flow so the name's full length can be measured
          without that length becoming the box's width. It carries the caller's own type through
          `className` on the wrapper, so it measures the face the input actually renders in. */}
      {maxWidth !== undefined ? (
        <span className={styles.sizer} ref={sizerRef} aria-hidden>
          {draft || ' '}
        </span>
      ) : null}
      <input
        ref={inputRef}
        className={styles.field}
        // Without this an input contributes its own 20-character intrinsic width, and that, not the
        // measurement, decides how wide the box is.
        size={1}
        value={draft}
        aria-label={label}
        style={fieldWidth === null ? undefined : { width: fieldWidth }}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setDraft(value);
            onEditingChange(false);
          }
          // A rename is a text field first: arrows, Home/End and ⌘A belong to the caret, not to
          // whatever list or menu is listening upstairs.
          e.stopPropagation();
        }}
        onBlur={commit}
        // The row underneath selects on click and reorders on drag. Neither should happen because
        // somebody put their cursor between two letters.
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      />
    </span>
  );
}
